import React, { useMemo } from 'react';

/**
 * Single source of truth for "does this visit have a follow-up?" — a visit carries
 * a bool flag (`follow_up_required`) plus the date (`follow_up_date`); either being
 * present means a follow-up is set. Keep every gate/label reading through this so
 * the completion guard and the patient-name label can't drift.
 */
export const isFollowUpSet = (visit) =>
  !!(visit?.follow_up_required || visit?.follow_up_date);

const _YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const _DURATION_RE = /^\s*(\d+)\s*(days?|d|weeks?|wks?|w|months?|mos?|mo|m)\s*$/i;

/**
 * Resolve a follow-up value to an absolute YYYY-MM-DD date (mirrors the backend
 * app/utils/follow_up.resolve_follow_up_date). The follow-up is always stored as
 * an absolute date; durations ("1 Week", "5d") are shortcuts resolved here.
 *
 *   falsy            → null
 *   "YYYY-MM-DD"     → unchanged
 *   "N day(s)/week(s)/month(s)" / "Nd"/"Nw"/"Nm" → baseDate (or today) + delta
 *   anything else    → null
 */
export function resolveFollowUpDate(value, baseDate) {
  if (!value) return null;
  const v = String(value).trim();
  if (_YMD_RE.test(v)) return v;
  const m = _DURATION_RE.exec(v);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  const base = (baseDate && _YMD_RE.test(String(baseDate)))
    ? new Date(`${baseDate}T00:00:00`)
    : new Date();
  if (unit.startsWith('d')) base.setDate(base.getDate() + n);
  else if (unit.startsWith('w')) base.setDate(base.getDate() + 7 * n);
  else base.setDate(base.getDate() + 30 * n); // months ≈ 30 days
  // Format the LOCAL date (not toISOString, which shifts to UTC and can land a
  // day early for IST users near midnight — the day-boundary trap).
  const y = base.getFullYear();
  const mo = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * FollowUpPicker — the shared follow-up control (preset chips + custom date).
 *
 * Single source for "pick a follow-up" across the app so the prescription form
 * (DigitalPrescriptionForm) and the visit-completion prompt (VisitCompletionPrompt)
 * can never set divergent follow-ups, and look/behave identically.
 *
 * Renders a fragment (the chips + the custom date input, no wrapper) so it drops
 * straight into the caller's own flex row alongside whatever label/header that
 * surface uses.
 *
 * Value model — an absolute YYYY-MM-DD date + a `required` flag:
 *   - click a preset / pick a custom date            → onChange(true, 'YYYY-MM-DD')
 *   - click the already-selected preset / clear date → onChange(false, null)
 *
 * Props:
 *   required  bool                       — whether a follow-up is currently set
 *   date      string|null                — selected YYYY-MM-DD date (null when none)
 *   onChange  (required, date) => void
 *   isDark    bool
 *   size      'compact' | 'comfortable'  — 'compact' (default) is the dense Rx-form
 *                                          styling; 'comfortable' is larger with solid
 *                                          backgrounds for standalone surfaces (modals).
 */
export default function FollowUpPicker({ required, date, onChange, isDark, size = 'compact' }) {
  const presets = useMemo(() => {
    const d = new Date();
    const fmt = (dt) => dt.toISOString().split('T')[0];
    const add = (days) => { const n = new Date(d); n.setDate(n.getDate() + days); return n; };
    return [
      { label: 'Today', date: fmt(d) },
      { label: 'Tomorrow', date: fmt(add(1)) },
      { label: '3D', date: fmt(add(3)) },
      { label: '1W', date: fmt(add(7)) },
    ];
  }, []);

  const teal = '#0d9488';
  const isPreset = presets.some((p) => p.date === date);
  // The custom date input is "active" only when a non-preset date is set.
  const customActive = !!required && !isPreset && !!date;
  const comfortable = size === 'comfortable';

  // Selected look (shared) — solid teal tint so it reads clearly in both modes.
  const selBg = comfortable
    ? (isDark ? 'rgba(13,148,136,0.28)' : '#ccfbf1')
    : (isDark ? 'rgba(13,148,136,0.08)' : 'rgba(13,148,136,0.04)');
  const selText = comfortable ? (isDark ? '#5eead4' : '#0f766e') : teal;

  // Idle look — compact stays faint/transparent (dense Rx form); comfortable gets a
  // solid background + visible border so it never disappears on the modal panel.
  const idleBg = comfortable ? (isDark ? '#0f172a' : '#f8fafc') : 'transparent';
  const idleBorder = comfortable
    ? (isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1')
    : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(180,160,120,0.18)');
  const idleText = comfortable ? (isDark ? '#e2e8f0' : '#334155') : (isDark ? '#6b7280' : '#9ca3af');

  const chipStyle = (sel) => (comfortable
    ? {
        padding: '0.35rem 0.75rem', borderRadius: '2rem',
        fontSize: '0.8125rem', fontWeight: sel ? 700 : 600,
        border: `1.5px solid ${sel ? teal : idleBorder}`,
        background: sel ? selBg : idleBg,
        color: sel ? selText : idleText,
        cursor: 'pointer', fontFamily: 'system-ui, sans-serif', transition: 'all 0.15s',
      }
    : {
        padding: '0.1rem 0.4rem', borderRadius: '2rem',
        fontSize: '0.5625rem', fontWeight: sel ? 700 : 400,
        border: sel ? `1.5px solid ${teal}` : `1px solid ${idleBorder}`,
        background: sel ? selBg : idleBg,
        color: sel ? selText : idleText,
        cursor: 'pointer', fontFamily: 'system-ui, sans-serif', transition: 'all 0.15s',
      });

  const dateStyle = comfortable
    ? {
        padding: '0.35rem 0.5rem', borderRadius: '0.5rem', fontSize: '0.8125rem',
        border: `1.5px solid ${customActive ? teal : idleBorder}`,
        background: customActive ? selBg : idleBg,
        color: customActive ? selText : idleText,
        fontFamily: 'system-ui, sans-serif', outline: 'none', cursor: 'pointer',
      }
    : {
        padding: '0.1rem 0.25rem', borderRadius: '0.25rem', fontSize: '0.5625rem',
        border: `1px solid ${customActive ? teal : idleBorder}`,
        background: customActive ? selBg : idleBg,
        color: customActive ? selText : idleText,
        fontFamily: 'system-ui, sans-serif', outline: 'none', cursor: 'pointer',
      };

  return (
    <>
      {presets.map((p) => {
        const sel = !!required && date === p.date;
        return (
          <button
            key={p.label}
            type="button"
            onClick={() => (sel ? onChange(false, null) : onChange(true, p.date))}
            style={chipStyle(sel)}
            className="rx-chip-hover"
          >{p.label}</button>
        );
      })}
      {/* Custom date — for any follow-up that isn't one of the presets */}
      <input
        type="date"
        value={customActive ? (date || '') : ''}
        onChange={(e) => (e.target.value ? onChange(true, e.target.value) : onChange(false, null))}
        min={new Date().toISOString().split('T')[0]}
        style={dateStyle}
      />
    </>
  );
}
